import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import "../styles/ProfileScreen.scss";
import {
  getAuthHeaders,
  getProfile,
  normalizeProjectRole,
  storeProfile,
  stripAccessFromRole,
} from "../utils/auth";
import { apiFetch } from "../utils/api";
import {
  buildTechnologyCatalogMeta,
  getTechnologyVisual,
  normalizeTechnologyLabel,
} from "../utils/technologies";
import { formatPresenceStatus } from "../utils/presence";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8080";
const TASKS_LINK_TEXT = "просмотреть задачу";

function appendUniqueTechnology(values, candidate) {
  const normalizedCandidate = normalizeTechnologyLabel(candidate);
  if (!normalizedCandidate) {
    return values;
  }

  const list = Array.isArray(values) ? values : [];
  if (list.some((item) => normalizeTechnologyLabel(item) === normalizedCandidate)) {
    return list;
  }

  return [...list, candidate];
}

function removeTechnology(values, candidate) {
  const normalizedCandidate = normalizeTechnologyLabel(candidate);
  return (Array.isArray(values) ? values : []).filter(
    (item) => normalizeTechnologyLabel(item) !== normalizedCandidate,
  );
}

function splitProfileList(value) {
  return String(value || "")
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter((item, index, items) =>
      item && items.findIndex((candidate) => candidate.toLowerCase() === item.toLowerCase()) === index
    );
}

function buildProfileDraft(profile) {
  return {
    first_name: profile?.first_name || "",
    last_name: profile?.last_name || "",
    full_name: profile?.full_name || "",
    username: profile?.username || "",
    date_of_birthday: profile?.date_of_birthday || "",
    number_of_phone: profile?.number_of_phone || "",
    photo_url: profile?.photo_url || "",
    github_url: profile?.github_url || "",
    technologies: (profile?.technologies || []).join(", "),
    achievements: (profile?.achievements || []).join("\n"),
  };
}

function normalizeGithubUrlInput(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return { isValid: true, value: "" };
  }

  let candidate = raw;
  if (candidate.startsWith("@")) {
    candidate = `https://github.com/${candidate.slice(1)}`;
  } else if (!/^https?:\/\//i.test(candidate)) {
    candidate = `https://${candidate}`;
  }

  let parsed;
  try {
    parsed = new URL(candidate);
  } catch (error) {
    return { isValid: false, value: "" };
  }

  const host = parsed.hostname.toLowerCase();
  if (host !== "github.com" && host !== "www.github.com") {
    return { isValid: false, value: "" };
  }

  const normalizedPath = parsed.pathname.replace(/\/+$/, "");
  if (!normalizedPath || normalizedPath === "/") {
    return { isValid: false, value: "" };
  }

  return {
    isValid: true,
    value: `https://github.com${normalizedPath}`,
  };
}

export default function ProfileScreen() {
  const [profile, setProfile] = useState(() => getProfile());
  const authHeaders = useMemo(() => getAuthHeaders(), []);
  const [githubDraft, setGithubDraft] = useState(
    () => String(profile?.github_url || ""),
  );
  const [githubError, setGithubError] = useState("");
  const [githubNotice, setGithubNotice] = useState("");
  const [isSavingGithub, setIsSavingGithub] = useState(false);

  const [projects, setProjects] = useState([]);
  const [projectsError, setProjectsError] = useState("");
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);

  const [userTechnologies, setUserTechnologies] = useState(() =>
    Array.isArray(profile?.technologies) ? profile.technologies : [],
  );
  const [technologyCatalog, setTechnologyCatalog] = useState({
    types: [],
    technologies: [],
  });
  const [isLoadingTechnologyCatalog, setIsLoadingTechnologyCatalog] = useState(true);
  const [technologyCatalogError, setTechnologyCatalogError] = useState("");
  const [technologyActionError, setTechnologyActionError] = useState("");
  const [savingTechnologyId, setSavingTechnologyId] = useState(0);
  const [isTechModalOpen, setIsTechModalOpen] = useState(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [profileDraft, setProfileDraft] = useState(() => buildProfileDraft(profile));
  const [profileEditError, setProfileEditError] = useState("");
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  useEffect(() => {
    let isActive = true;

    apiFetch(`${API_BASE}/technologies`, { headers: authHeaders })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return response.json();
      })
      .then((data) => {
        if (!isActive) {
          return;
        }
        setTechnologyCatalog({
          types: Array.isArray(data?.types) ? data.types : [],
          technologies: Array.isArray(data?.technologies) ? data.technologies : [],
        });
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setTechnologyCatalogError("Не удалось загрузить каталог технологий");
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setIsLoadingTechnologyCatalog(false);
      });

    return () => {
      isActive = false;
    };
  }, [authHeaders]);

  useEffect(() => {
    const profileId = profile?.telegram_id ? String(profile.telegram_id) : "";
    const profileUsername = profile?.username
      ? profile.username.replace(/^@/, "").toLowerCase()
      : "";

    if (!profileId && !profileUsername) {
      return;
    }

    let isActive = true;
    apiFetch(`${API_BASE}/get_users`, { headers: authHeaders })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return response.json();
      })
      .then((data) => {
        if (!isActive) {
          return;
        }
        const users = Array.isArray(data) ? data : [];
        const currentUser = users.find((user) => {
          const userId = user?.telegram_id ? String(user.telegram_id) : "";
          const userUsername = user?.username
            ? String(user.username).replace(/^@/, "").toLowerCase()
            : "";
          return (
            (profileId && userId === profileId) ||
            (profileUsername && userUsername === profileUsername)
          );
        });

        if (!currentUser) {
          return;
        }

        const nextTechnologies = Array.isArray(currentUser.technologies)
          ? currentUser.technologies
          : [];
        setUserTechnologies(nextTechnologies);

        setProfile((prev) => {
          const nextProfile = { ...(prev || {}), ...currentUser };
          storeProfile(nextProfile);
          return nextProfile;
        });
      })
      .catch(() => {});

    return () => {
      isActive = false;
    };
  }, [authHeaders, profile?.telegram_id, profile?.username]);

  const derivedProfile = useMemo(() => {
    const firstName =
      profile?.first_name ||
      (profile?.full_name ? profile.full_name.split(" ")[0] : "");
    const lastName =
      profile?.last_name ||
      (profile?.full_name
        ? profile.full_name.split(" ").slice(1).join(" ")
        : "");
    const fullName =
      profile?.full_name ||
      [profile?.first_name, profile?.last_name].filter(Boolean).join(" ");

    return {
      avatar:
        profile?.photo_url ||
        "https://images.unsplash.com/photo-1504593811423-6dd665756598?auto=format&fit=crop&w=400&q=70",
      handle: profile?.username ? `@${profile.username}` : "—",
      fullName: fullName || "—",
      firstName: firstName || "—",
      lastName: lastName || "—",
      dateOfBirthday: profile?.date_of_birthday || "—",
      role: stripAccessFromRole(profile?.role || "") || "—",
      telegramID: profile?.telegram_id || "—",
    };
  }, [profile]);

  useEffect(() => {
    const username = profile?.username?.replace(/^@/, "");
    if (!username) {
      setProjects([]);
      setIsLoadingProjects(false);
      return;
    }

    let isActive = true;
    setIsLoadingProjects(true);
    setProjectsError("");

    apiFetch(`${API_BASE}/projects?username=${encodeURIComponent(username)}`, {
      headers: authHeaders,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return response.json();
      })
      .then((data) => {
        if (!isActive) {
          return;
        }
        setProjects(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setProjectsError("Не удалось загрузить проекты");
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setIsLoadingProjects(false);
      });

    return () => {
      isActive = false;
    };
  }, [authHeaders, profile?.username]);

  const profilePresenceStatus = useMemo(
    () => formatPresenceStatus(profile?.is_online, profile?.last_seen),
    [profile?.is_online, profile?.last_seen],
  );
  const isProfileOnline = Boolean(profile?.is_online);
  const profileFields = useMemo(
    () => [
      { label: "Дата рождения", value: derivedProfile.dateOfBirthday },
      { label: "Должность", value: derivedProfile.role },
      { label: "TelegramID", value: derivedProfile.telegramID },
    ],
    [derivedProfile],
  );

  const normalizedUsername = profile?.username
    ? profile.username.replace(/^@/, "").toLowerCase()
    : "";

  const technologyCatalogMeta = useMemo(
    () => buildTechnologyCatalogMeta(technologyCatalog),
    [technologyCatalog],
  );

  const selectedTechnologySet = useMemo(
    () =>
      new Set(
        (Array.isArray(userTechnologies) ? userTechnologies : [])
          .map((item) => normalizeTechnologyLabel(item))
          .filter(Boolean),
      ),
    [userTechnologies],
  );

  const normalizedStoredGithub = useMemo(() => {
    const normalized = normalizeGithubUrlInput(profile?.github_url || "");
    return normalized.isValid ? normalized.value : "";
  }, [profile?.github_url]);

  const groupedCatalog = useMemo(() => {
    const groupsMap = {};
    const types = Array.isArray(technologyCatalog?.types)
      ? technologyCatalog.types
      : [];
    const technologies = Array.isArray(technologyCatalog?.technologies)
      ? technologyCatalog.technologies
      : [];

    types.forEach((type) => {
      const typeId = Number(type?.id);
      if (!Number.isFinite(typeId)) {
        return;
      }
      const typeMeta = technologyCatalogMeta.typeById[typeId] || {
        icon: "##",
        hue: 200,
      };
      groupsMap[typeId] = {
        id: typeId,
        label: String(type?.label || "Other"),
        icon: typeMeta.icon,
        hue: typeMeta.hue,
        items: [],
      };
    });

    technologies.forEach((technology) => {
      const typeId = Number(technology?.type_id);
      const label = String(technology?.label || "").trim();
      const techId = Number(technology?.id);
      if (!label || !Number.isFinite(techId)) {
        return;
      }

      if (!groupsMap[typeId]) {
        const fallback = getTechnologyVisual(label, technologyCatalogMeta);
        groupsMap[typeId] = {
          id: typeId || `fallback-${fallback.typeLabel}`,
          label: fallback.typeLabel || "Other",
          icon: fallback.icon,
          hue: fallback.hue,
          items: [],
        };
      }

      groupsMap[typeId].items.push({
        id: techId,
        label,
      });
    });

    return Object.values(groupsMap)
      .filter((group) => Array.isArray(group.items) && group.items.length > 0)
      .map((group) => ({
        ...group,
        items: group.items.sort((a, b) => a.label.localeCompare(b.label, "ru")),
      }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label), "ru"));
  }, [technologyCatalog, technologyCatalogMeta]);

  useEffect(() => {
    setGithubDraft(String(profile?.github_url || ""));
  }, [profile?.github_url]);

  const openProfileEditor = () => {
    setProfileDraft(buildProfileDraft(profile));
    setProfileEditError("");
    setIsEditModalOpen(true);
  };

  const handleProfileDraftChange = (field, value) => {
    setProfileDraft((prev) => ({ ...prev, [field]: value }));
    if (profileEditError) {
      setProfileEditError("");
    }
  };

  const handleProfileSave = (event) => {
    event.preventDefault();
    if (isSavingProfile) {
      return;
    }

    const github = normalizeGithubUrlInput(profileDraft.github_url);
    if (!github.isValid) {
      setProfileEditError("Введите корректную ссылку GitHub");
      return;
    }

    const payload = {
      ...profileDraft,
      username: String(profileDraft.username || "").trim().replace(/^@/, ""),
      github_url: github.value,
      technologies: splitProfileList(profileDraft.technologies),
      achievements: splitProfileList(profileDraft.achievements),
    };

    setIsSavingProfile(true);
    setProfileEditError("");
    apiFetch(`${API_BASE}/users/me`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return response.json();
      })
      .then((updatedProfile) => {
        const next = { ...(profile || {}), ...updatedProfile };
        setProfile(next);
        setUserTechnologies(Array.isArray(next.technologies) ? next.technologies : []);
        storeProfile(next);
        setIsEditModalOpen(false);
      })
      .catch(() => setProfileEditError("Не удалось сохранить профиль"))
      .finally(() => setIsSavingProfile(false));
  };

  const handleGithubSave = () => {
    if (isSavingGithub) {
      return;
    }

    setGithubError("");
    setGithubNotice("");

    const normalized = normalizeGithubUrlInput(githubDraft);
    if (!normalized.isValid) {
      setGithubError("Введите ссылку формата https://github.com/username");
      return;
    }

    setIsSavingGithub(true);
    apiFetch(`${API_BASE}/users/github_url`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify({ github_url: normalized.value }),
    })
      .then((response) => {
        if (!response.ok && response.status !== 204) {
          throw new Error("failed");
        }
      })
      .then(() => {
        setProfile((prev) => {
          const next = {
            ...(prev || {}),
            github_url: normalized.value,
          };
          storeProfile(next);
          return next;
        });
        setGithubDraft(normalized.value);
        setGithubNotice(
          normalized.value ? "Ссылка GitHub обновлена" : "Ссылка GitHub очищена",
        );
      })
      .catch(() => {
        setGithubError("Не удалось сохранить ссылку GitHub");
      })
      .finally(() => setIsSavingGithub(false));
  };

  const handleTechnologySelect = (technologyId, technologyLabel) => {
    if (!technologyId || savingTechnologyId) {
      return;
    }

    const normalized = normalizeTechnologyLabel(technologyLabel);
    if (selectedTechnologySet.has(normalized)) {
      return;
    }

    setTechnologyActionError("");
    setSavingTechnologyId(technologyId);

    apiFetch(`${API_BASE}/users/technologies`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify({ tech_id: technologyId }),
    })
      .then((response) => {
        if (!response.ok && response.status !== 204) {
          throw new Error("failed");
        }
      })
      .then(() => {
        setUserTechnologies((prev) => appendUniqueTechnology(prev, technologyLabel));
        setProfile((prev) => {
          const next = {
            ...(prev || {}),
            technologies: appendUniqueTechnology(
              Array.isArray(prev?.technologies) ? prev.technologies : [],
              technologyLabel,
            ),
          };
          storeProfile(next);
          return next;
        });
      })
      .catch(() => {
        setTechnologyActionError("Не удалось сохранить технологию");
      })
      .finally(() => {
        setSavingTechnologyId(0);
      });
  };

  const handleTechnologyDelete = (technologyId, technologyLabel) => {
    if (!technologyId || Boolean(savingTechnologyId)) {
      return;
    }

    setTechnologyActionError("");
    setSavingTechnologyId(technologyId);

    apiFetch(`${API_BASE}/users/technologies`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify({ tech_id: technologyId }),
    })
      .then((response) => {
        if (!response.ok && response.status !== 204) {
          throw new Error("failed");
        }
      })
      .then(() => {
        setUserTechnologies((prev) => removeTechnology(prev, technologyLabel));
        setProfile((prev) => {
          const next = {
            ...(prev || {}),
            technologies: removeTechnology(
              Array.isArray(prev?.technologies) ? prev.technologies : [],
              technologyLabel,
            ),
          };
          storeProfile(next);
          return next;
        });
      })
      .catch(() => {
        setTechnologyActionError("Не удалось удалить технологию");
      })
      .finally(() => {
        setSavingTechnologyId(0);
      });
  };

  useEffect(() => {
    if (!isTechModalOpen) {
      return;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsTechModalOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isTechModalOpen]);

  const technologyModal = isTechModalOpen
    ? createPortal(
        <div
          className="profile-tech-modal-overlay"
          onClick={() => setIsTechModalOpen(false)}
        >
          <div
            className="profile-tech-modal"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="profile-tech-modal__header">
              <div>
                <p className="profile-tech-modal__eyebrow">Технологии</p>
                <h3 className="profile-tech-modal__title">Выбор стека</h3>
              </div>
              <button
                type="button"
                className="profile-tech-modal__close"
                onClick={() => setIsTechModalOpen(false)}
                aria-label="Закрыть"
              >
                <span />
                <span />
              </button>
            </div>

            <section className="profile-tech-picker profile-tech-picker--modal">
              <div className="profile-tech-picker__head">
                <h3>Отметьте технологии</h3>
                <p>Выбранные технологии будут видны другим участникам.</p>
              </div>

              {isLoadingTechnologyCatalog && (
                <p className="profile-tech-picker__hint">Загрузка каталога...</p>
              )}
              {!isLoadingTechnologyCatalog && technologyCatalogError && (
                <p className="profile-tech-picker__error">{technologyCatalogError}</p>
              )}
              {!isLoadingTechnologyCatalog &&
                !technologyCatalogError &&
                groupedCatalog.length === 0 && (
                  <p className="profile-tech-picker__hint">
                    Каталог технологий пока пуст.
                  </p>
                )}
              {!isLoadingTechnologyCatalog &&
                !technologyCatalogError &&
                groupedCatalog.length > 0 && (
                  <div className="profile-tech-picker__groups">
                    {groupedCatalog.map((group) => (
                      <article className="profile-tech-picker__group" key={group.id}>
                        <div className="profile-tech-picker__group-header">
                          <span
                            className="profile-tech-picker__group-icon"
                            style={{ "--tech-hue": group.hue }}
                          >
                            {group.icon}
                          </span>
                          <h4>{group.label}</h4>
                        </div>
                        <div className="profile-tech-picker__items">
                          {group.items.map((technology) => {
                            const isSelected = selectedTechnologySet.has(
                              normalizeTechnologyLabel(technology.label),
                            );
                            const isSaving = savingTechnologyId === technology.id;
                            return (
                              <button
                                type="button"
                                key={`catalog-tech-${technology.id}`}
                                className={`profile-tech-picker__item${
                                  isSelected ? " is-selected" : ""
                                }`}
                                onClick={() =>
                                  isSelected
                                    ? handleTechnologyDelete(
                                        technology.id,
                                        technology.label,
                                      )
                                    : handleTechnologySelect(
                                        technology.id,
                                        technology.label,
                                      )
                                }
                                disabled={Boolean(savingTechnologyId)}
                              >
                                <span>{technology.label}</span>
                                <small>
                                  {isSelected
                                    ? isSaving
                                      ? "Удаляем..."
                                      : "Убрать"
                                    : isSaving
                                      ? "Сохраняем..."
                                      : "Добавить"}
                                </small>
                              </button>
                            );
                          })}
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              {technologyActionError && (
                <p className="profile-tech-picker__error">{technologyActionError}</p>
              )}
            </section>
            <div className="profile-tech-modal__actions">
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  const profileEditModal = isEditModalOpen
    ? createPortal(
        <div className="profile-tech-modal-overlay" onClick={() => setIsEditModalOpen(false)}>
          <div
            className="profile-tech-modal profile-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-edit-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="profile-tech-modal__header">
              <div>
                <p className="profile-tech-modal__eyebrow">Личный профиль</p>
                <h3 className="profile-tech-modal__title" id="profile-edit-title">
                  Редактировать информацию
                </h3>
              </div>
              <button
                type="button"
                className="profile-tech-modal__close"
                onClick={() => setIsEditModalOpen(false)}
                aria-label="Закрыть"
              >
                <span />
                <span />
              </button>
            </div>
            <form className="profile-edit-form" onSubmit={handleProfileSave}>
              <label>Имя<input value={profileDraft.first_name} onChange={(e) => handleProfileDraftChange("first_name", e.target.value)} /></label>
              <label>Фамилия<input value={profileDraft.last_name} onChange={(e) => handleProfileDraftChange("last_name", e.target.value)} /></label>
              <label className="profile-edit-form__full">ФИО<input value={profileDraft.full_name} onChange={(e) => handleProfileDraftChange("full_name", e.target.value)} /></label>
              <label>Дата рождения<input value={profileDraft.date_of_birthday} onChange={(e) => handleProfileDraftChange("date_of_birthday", e.target.value)} placeholder="ДД.ММ.ГГГГ" /></label>
              <label>Телефон<input value={profileDraft.number_of_phone} onChange={(e) => handleProfileDraftChange("number_of_phone", e.target.value)} /></label>
              <label>GitHub<input value={profileDraft.github_url} onChange={(e) => handleProfileDraftChange("github_url", e.target.value)} placeholder="https://github.com/username" /></label>
              <label className="profile-edit-form__full">Ссылка на фото<input type="url" value={profileDraft.photo_url} onChange={(e) => handleProfileDraftChange("photo_url", e.target.value)} /></label>
              <label className="profile-edit-form__full">Стек<textarea value={profileDraft.technologies} onChange={(e) => handleProfileDraftChange("technologies", e.target.value)} placeholder="React, Go, PostgreSQL" /></label>
              <label className="profile-edit-form__full">Достижения<textarea value={profileDraft.achievements} onChange={(e) => handleProfileDraftChange("achievements", e.target.value)} placeholder="Каждое достижение — с новой строки" /></label>
              {profileEditError && <p className="profile-edit-form__error">{profileEditError}</p>}
              <div className="profile-edit-form__actions">
                <button type="submit" disabled={isSavingProfile}>{isSavingProfile ? "Сохраняем..." : "Сохранить"}</button>
                <button type="button" onClick={() => setIsEditModalOpen(false)} disabled={isSavingProfile}>Отмена</button>
              </div>
            </form>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <section className="profile-screen">
      <div className="profile-screen__inner">
        <aside className="profile-card">
          <span
            className={`profile-card__presence-badge${
              isProfileOnline ? " is-online" : " is-offline"
            }`}
          >
            {profilePresenceStatus}
          </span>
          <div className="profile-card__avatar">
            <img
              src={derivedProfile.avatar}
              alt="Портрет пользователя"
              loading="lazy"
            />
          </div>
          <p className="profile-card__handle">{derivedProfile.handle}</p>
          <p className="profile-card__name">{derivedProfile.fullName}</p>
          <button type="button" className="profile-card__edit" onClick={openProfileEditor}>
            Редактировать профиль
          </button>
          <div className="profile-card__scroll">
            <div className="profile-card__fields" aria-label="личные данные">
              {profileFields.map((field) => (
                <article className="profile-card__field" key={field.label}>
                  <span className="profile-card__label">{field.label}:</span>
                  <span className="profile-card__value">{field.value}</span>
                </article>
              ))}
            </div>

            <section className="profile-card__github" aria-label="GitHub профиль">
              <div className="profile-card__github-head">
                <span className="profile-card__github-icon">GH</span>
                <div>
                  <p className="profile-card__github-title">GitHub профиль</p>
                  <p className="profile-card__github-subtitle">
                    Добавьте ссылку, чтобы участники быстро находили ваш аккаунт.
                  </p>
                </div>
              </div>
              <div className="profile-card__github-controls">
                <input
                  type="url"
                  value={githubDraft}
                  onChange={(event) => {
                    setGithubDraft(event.target.value);
                    if (githubError) {
                      setGithubError("");
                    }
                    if (githubNotice) {
                      setGithubNotice("");
                    }
                  }}
                  placeholder="https://github.com/username"
                />
                <button
                  type="button"
                  onClick={handleGithubSave}
                  disabled={isSavingGithub}
                >
                  {isSavingGithub ? "Сохранение..." : "Сохранить"}
                </button>
              </div>
              <div className="profile-card__github-foot">
                {normalizedStoredGithub && (
                  <a
                    href={normalizedStoredGithub}
                    target="_blank"
                    rel="noreferrer"
                    className="profile-card__github-link"
                  >
                    Открыть профиль
                  </a>
                )}
                {githubNotice && (
                  <span className="profile-card__github-status is-success">
                    {githubNotice}
                  </span>
                )}
                {githubError && (
                  <span className="profile-card__github-status is-error">
                    {githubError}
                  </span>
                )}
              </div>
            </section>

            <div className="profile-card__technologies">
              <div className="profile-card__technologies-header">
                <span>Мой стек</span>
                <div className="profile-card__tech-actions">
                  <strong>{userTechnologies.length}</strong>
                  <button
                    type="button"
                    className="profile-card__tech-add"
                    onClick={() => setIsTechModalOpen(true)}
                    aria-label="Добавить технологии"
                  >
                    +
                  </button>
                </div>
              </div>
              {userTechnologies.length === 0 ? (
                <p className="profile-card__tech-hint">Пока не выбраны технологии.</p>
              ) : (
                <div className="profile-card__technologies-list">
                  {userTechnologies.map((technology) => {
                    const visual = getTechnologyVisual(technology, technologyCatalogMeta);
                    return (
                      <span
                        className="tech-badge"
                        key={`profile-tech-${technology}`}
                        style={{ "--tech-hue": visual.hue }}
                      >
                        <span className="tech-badge__icon">{visual.icon}</span>
                        <span>{technology}</span>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            <section className="profile-card__achievements" aria-label="Достижения">
              <div className="profile-card__technologies-header">
                <span>Мои достижения</span>
                <strong>{Array.isArray(profile?.achievements) ? profile.achievements.length : 0}</strong>
              </div>
              {Array.isArray(profile?.achievements) && profile.achievements.length > 0 ? (
                <ul>
                  {profile.achievements.map((achievement, index) => (
                    <li key={`${achievement}-${index}`}>{achievement}</li>
                  ))}
                </ul>
              ) : (
                <p className="profile-card__tech-hint">Добавьте первое достижение в редакторе профиля.</p>
              )}
            </section>

          </div>
          <p className="itc-info">
            © {new Date().getFullYear()} Студенческое IT-сообщество
          </p>
        </aside>

        <section className="profile-projects">
          <div className="profile-projects__header">
            <span className="profile-projects__badge">Мои проекты</span>
          </div>

          <div className="profile-projects__list">
            {isLoadingProjects && (
              <article className="project-card" aria-busy="true">
                <div className="project-card__main">
                  <p className="project-card__title">Загрузка...</p>
                  <p className="project-card__subtitle">подождите немного</p>
                </div>
              </article>
            )}
            {!isLoadingProjects && projectsError && (
              <article className="project-card">
                <div className="project-card__main">
                  <p className="project-card__title">Ошибка</p>
                  <p className="project-card__subtitle">{projectsError}</p>
                </div>
              </article>
            )}
            {!isLoadingProjects && !projectsError && projects.length === 0 && (
              <article className="project-card">
                <div className="project-card__main">
                  <p className="project-card__title">Проекты не найдены</p>
                  <p className="project-card__subtitle">
                    нет связанных проектов для профиля
                  </p>
                </div>
              </article>
            )}
            {!isLoadingProjects &&
              !projectsError &&
              projects.length > 0 &&
              projects.map((project) => {
                const members = Array.isArray(project.members)
                  ? project.members
                  : [];
                const currentMember = members.find(
                  (member) =>
                    member.username?.toLowerCase() === normalizedUsername,
                );
                const role =
                  normalizeProjectRole(currentMember?.role || "") || "участник";
                return (
                  <Link
                    to={`/projects/${project.id}?from=profile`}
                    className="project-link"
                    key={project.id}
                  >
                    <article className="project-card">
                      <div className="project-card__main">
                        <p className="project-card__title">{project.title}</p>
                        <p className="project-card__subtitle">
                          {TASKS_LINK_TEXT}
                        </p>
                      </div>
                      <div className="project-card__meta">
                        <p>
                          <span>статус:</span> {project.status || "—"}
                        </p>
                        <p>
                          <span>участники:</span> {members.length}
                        </p>
                        <p>
                          <span>роль:</span> {role}
                        </p>
                      </div>
                    </article>
                  </Link>
                );
              })}
          </div>
        </section>
      </div>
      {technologyModal}
      {profileEditModal}
    </section>
  );
}
